import {
  ASSET_TYPES,
  MONITOR_STATUS_LABELS,
  MONITOR_STATUSES,
  subject,
  type MonitoredDevice,
  type MonitoringEvent,
  type MonitoringSummary,
  type MonitorStatusCode,
  type Paginated,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip, StatusIndicator } from '@ftthnms/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Activity, ChevronLeft, ChevronRight, ExternalLink, Search } from 'lucide-react';
import { useEffect, useState, type MouseEvent } from 'react';
import { AssetDrawer } from '../components/AssetDrawer';
import { AssetForm } from '../components/AssetForm';
import { EventFeed } from '../components/MonitoringPanel';
import { useToast } from '../components/Toast';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, nf, nf1, qs, send } from '../lib/api';
import { ago, toIndicator, uptime, webUiUrl } from '../lib/monitoring';
import { useRealtimeConnected } from '../lib/realtime';
import { useSession } from '../lib/session';
import { useNavigate } from '@tanstack/react-router';
import type { AssetDetail } from '@ftthnms/shared';

const KPI_TONE: Record<MonitorStatusCode, string> = {
  online: 'success',
  warning: 'warning',
  offline: 'danger',
  maintenance: 'purple',
  unknown: 'primary',
};
const MONITORED_TYPES = ASSET_TYPES.filter((t) => ['OLT', 'SERVER', 'AP', 'CPE'].includes(t.code));

type Panel = { kind: 'view'; id: string } | { kind: 'edit'; asset: AssetDetail };

/** Device status dashboard (MON-08): summary, device table with live updates, recent status changes. */
export function MonitoringPage() {
  const { area, ability } = useSession();
  const live = useRealtimeConnected();
  const toast = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [pinging, setPinging] = useState<string | null>(null);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, status, type, area]);

  const summary = useQuery({
    queryKey: ['monitoring', 'summary', area],
    queryFn: () => api<MonitoringSummary>(`/api/v1/monitoring/summary${qs({ area })}`),
    refetchInterval: 60_000,
  });
  const events = useQuery({
    queryKey: ['monitoring', 'events', area],
    queryFn: () => api<MonitoringEvent[]>(`/api/v1/monitoring/events${qs({ area })}`),
  });
  const devices = useQuery({
    queryKey: ['monitoring', 'devices', { area, q: debouncedQ, status, type, page }],
    queryFn: () =>
      api<Paginated<MonitoredDevice>>(
        `/api/v1/monitoring/devices${qs({ area, q: debouncedQ, status, type, page, pageSize })}`,
      ),
    placeholderData: keepPreviousData,
  });
  const data = devices.data;
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  const canPingAny = ability.can('ping', 'Monitoring');

  async function ping(d: MonitoredDevice) {
    setPinging(d.assetId);
    try {
      const r = await send<{ status: MonitorStatusCode; latencyMs: number | null }>(
        'POST',
        `/api/v1/monitoring/assets/${d.assetId}/ping`,
      );
      toast({
        tone: r.status === 'online' ? 'success' : r.status === 'offline' ? 'danger' : 'warning',
        text: `${d.code}: ${MONITOR_STATUS_LABELS[r.status]}${r.latencyMs !== null ? ` · ${nf1.format(r.latencyMs)} ms` : ''}`,
      });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setPinging(null);
    }
  }

  async function pingAll() {
    try {
      const r = await send<{ queued: number }>('POST', '/api/v1/monitoring/ping-bulk', {
        area,
        status: status || undefined,
      });
      toast({
        tone: 'info',
        text: `${nf.format(r.queued)} perangkat sedang di-ping; hasilnya masuk langsung ke tabel.`,
      });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    }
  }

  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div className="fn-page-body">
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {MONITOR_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className="fn-card fn-kpi fn-card--interactive"
            aria-pressed={status === s}
            onClick={() => setStatus((cur) => (cur === s ? '' : s))}
            style={{
              textAlign: 'left',
              cursor: 'pointer',
              outline: status === s ? '2px solid var(--primary)' : undefined,
            }}
          >
            <div className="fn-kpi__head">
              <span className="fn-kpi__label">{MONITOR_STATUS_LABELS[s]}</span>
              <span className={`fn-tile fn-tile--${KPI_TONE[s]}`}>
                <Activity className="i" aria-hidden />
              </span>
            </div>
            <div className="fn-kpi__value">
              <span className="fn-kpi__number">
                {summary.isPending ? '–' : nf.format(summary.data?.counts[s] ?? 0)}
              </span>
              <span className="fn-kpi__unit">perangkat</span>
            </div>
          </button>
        ))}
      </div>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: 'minmax(0, 1fr) 320px', alignItems: 'start' }}
      >
        <section className="fn-card" style={{ overflow: 'hidden' }}>
          <div className="fn-toolbar">
            <div className="fn-input-group" style={{ flex: 1, maxWidth: 280, minWidth: 160 }}>
              <Search className="i" aria-hidden />
              <input
                className="fn-input"
                aria-label="Cari perangkat"
                placeholder="Cari nama, kode, IP…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <select
              className="fn-input"
              aria-label="Jenis"
              style={{ width: 'auto' }}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Semua jenis</option>
              {MONITORED_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
            <select
              className="fn-input"
              aria-label="Status"
              style={{ width: 'auto' }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Semua status</option>
              {MONITOR_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {MONITOR_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <span className="fn-toolbar__spacer" />
            <Badge tone={live ? 'success' : 'neutral'} live={live} dot>
              {live ? 'Live' : 'Offline'}
            </Badge>
            {canPingAny && (
              <Button size="sm" icon={<Activity className="i" aria-hidden />} onClick={() => void pingAll()}>
                Ping semua
              </Button>
            )}
          </div>

          {data && data.total === 0 ? (
            <div style={{ padding: 20 }}>
              <EmptyState
                icon={<Activity className="i i--xl" aria-hidden />}
                title={
                  status || debouncedQ || type
                    ? 'Tidak ada perangkat yang cocok'
                    : 'Belum ada perangkat yang dipantau'
                }
                text="Perangkat dipantau bila punya alamat IP (OLT, Server/Router, Access Point otomatis; CPE bila dinyalakan di tab Monitoring)."
              />
            </div>
          ) : (
            <div className="fn-table-wrap">
              <table className="fn-table">
                <thead>
                  <tr>
                    <th>Perangkat</th>
                    <th>Status</th>
                    <th>IP</th>
                    <th className="is-num">Latensi</th>
                    <th className="is-num">Loss</th>
                    <th className="is-num">CPU</th>
                    <th className="is-num">Memori</th>
                    <th>Uptime</th>
                    <th>Dicek</th>
                    <th className="is-end">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((d) => {
                    const web = webUiUrl(d.ip, d.httpsPort, d.httpPort);
                    return (
                      <tr
                        key={d.assetId}
                        onClick={() => setPanel({ kind: 'view', id: d.assetId })}
                        style={{ cursor: 'pointer', opacity: d.enabled ? 1 : 0.55 }}
                        aria-selected={panel?.kind === 'view' && panel.id === d.assetId}
                      >
                        <td>
                          <div className="fn-asset">
                            <AssetGlyph type={d.typeCode} />
                            <div>
                              <div className="fn-asset__name">{d.name}</div>
                              <div className="fn-asset__code">
                                {d.code}
                                {d.areaCode && (
                                  <span style={{ marginLeft: 6 }}>
                                    <RegionChip code={d.areaCode} />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <StatusIndicator
                            status={toIndicator(d.status)}
                            meta={d.since ? ago(d.since) : undefined}
                          />
                          {!d.enabled && <div className="fn-hint">pemantauan mati</div>}
                        </td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                          {web ? (
                            <a
                              className="fn-link"
                              href={web}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={`Buka web UI ${web}`}
                            >
                              {d.ip}{' '}
                              <ExternalLink
                                className="i i--sm"
                                aria-hidden
                                style={{ display: 'inline', verticalAlign: '-2px' }}
                              />
                            </a>
                          ) : (
                            d.ip
                          )}
                        </td>
                        <td className="is-num">
                          {d.latencyMs == null ? '—' : `${nf1.format(d.latencyMs)} ms`}
                        </td>
                        <td className="is-num">{d.lossPct == null ? '—' : `${nf.format(d.lossPct)}%`}</td>
                        <td className="is-num">
                          {d.cpuPct == null ? '—' : `${nf.format(Math.round(d.cpuPct))}%`}
                        </td>
                        <td className="is-num">
                          {d.memPct == null ? '—' : `${nf.format(Math.round(d.memPct))}%`}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{uptime(d.uptimeS)}</td>
                        <td style={{ whiteSpace: 'nowrap' }} title={d.lastError ?? undefined}>
                          {ago(d.lastCheckedAt)}
                        </td>
                        <td className="is-end">
                          {ability.can('ping', subject('Monitoring', { areaCode: d.areaCode })) && (
                            <Button
                              size="sm"
                              loading={pinging === d.assetId}
                              onClick={stop(() => void ping(d))}
                            >
                              Ping
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="fn-table-footer">
            <div>
              <strong>{nf.format(data?.total ?? 0)}</strong> perangkat · status diperbarui otomatis
            </div>
            <nav className="fn-pagination" aria-label="Halaman">
              <button
                type="button"
                className="fn-page"
                disabled={page <= 1}
                aria-label="Sebelumnya"
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="i i--sm" aria-hidden />
              </button>
              <span className="fn-page" aria-current="page">
                {page}
              </span>
              <span className="fn-hint">dari {lastPage}</span>
              <button
                type="button"
                className="fn-page"
                disabled={page >= lastPage}
                aria-label="Selanjutnya"
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="i i--sm" aria-hidden />
              </button>
            </nav>
          </div>
        </section>

        <section className="fn-card">
          <div className="fn-card__body">
            {events.data && events.data.length > 0 ? (
              <EventFeed
                events={events.data.slice(0, 15)}
                showAsset
                onOpen={(id) => setPanel({ kind: 'view', id })}
              />
            ) : (
              <p className="fn-hint">Belum ada perubahan status.</p>
            )}
          </div>
        </section>
      </div>

      {panel?.kind === 'view' && (
        <AssetDrawer
          key={panel.id}
          id={panel.id}
          initialTab="monitoring"
          onClose={() => setPanel(null)}
          onEdit={(asset) => setPanel({ kind: 'edit', asset })}
          onOpenAsset={(id) => setPanel({ kind: 'view', id })}
          onFocus={(asset) => void navigate({ to: '/map', search: { focus: asset.id } })}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <AssetForm
          key={`edit-${panel.asset.id}`}
          mode="edit"
          asset={panel.asset}
          onClose={() => setPanel({ kind: 'view', id: panel.asset.id })}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
    </div>
  );
}
